import { ComponentProps, forwardRef } from 'react';
import cn from '../../app/utils/cn';

export type IconButtonVariant = 'ghost' | 'danger';
export type IconButtonSize = 'sm' | 'md' | 'lg';

interface IIconButtonProps extends ComponentProps<'button'> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
}

const sizeClass: Record<IconButtonSize, string> = {
  sm: 'h-6 w-6 p-1',
  md: 'h-8 w-8 p-1.5',
  lg: 'h-10 w-10 p-2',
};

const variantClass: Record<IconButtonVariant, string> = {
  ghost: 'text-text-secondary hover:text-text-primary hover:bg-hover-medium',
  danger:
    'text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300',
};

export const IconButton = forwardRef<HTMLButtonElement, IIconButtonProps>(
  (
    {
      type = 'button',
      variant = 'ghost',
      size = 'sm',
      className,
      disabled,
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center rounded transition-colors outline-none',
        'focus-visible:ring-2 focus-visible:ring-green-500/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        sizeClass[size],
        variantClass[variant],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  ),
);

IconButton.displayName = 'IconButton';
